import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faPaperPlane, faTimes } from '@fortawesome/free-solid-svg-icons';
import llmAxiosInstance from 'helpers/llmApi';
import toast from 'react-hot-toast';
import { useAtom } from 'jotai';
import { user_info } from 'jotai/UserJotaiInstance';
import { UserInfo } from 'interfaces/user';
import KairosLogo from 'assets/img/logos/KairosIQ-LogoOnly.png';

interface ChatMessage {
  type: 'user' | 'ai';
  message: string;
  timestamp: string;
  isTyping?: boolean;
}

interface WorkflowAlert {
  workflow_id: string;
  workflow_type: string;
  workflow_stage: string;
  due_date: string;
  hours_until_due: number;
  priority: 'critical' | 'high' | 'medium' | 'low';
  status: string;
  alert_type: 'due_soon' | 'overdue';
  message: string;
  actions: Array<{
    action: string;
    label: string;
    icon: string;
  }>;
  metadata: {
    created_at: string;
    workflow_progress: number;
    template_name: string;
  };
}

interface SimpleLeadAIProps {
  leadId: string;
  leadFirstName: string;
  leadLastName?: string;
  userId?: string;
  workflowAlerts?: WorkflowAlert[];
  onWorkflowAction?: (alert: WorkflowAlert, action: string) => void;
}

const SimpleLeadAI: React.FC<SimpleLeadAIProps> = ({
  leadId,
  leadFirstName,
  leadLastName,
  userId,
  workflowAlerts = [],
  onWorkflowAction
}) => {
  console.log('🚀 [SIMPLE LEAD AI] Rendering with full context:', {
    leadId,
    leadFirstName,
    leadLastName,
    userId,
    workflowAlertsCount: workflowAlerts?.length || 0,
    timestamp: new Date().toISOString()
  });
  console.log('✅✅✅ [SIMPLE LEAD AI] THIS IS THE CORRECT COMPONENT! ✅✅✅');
  const [userInfo] = useAtom<UserInfo>(user_info);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Enhanced logging for userInfo
  useEffect(() => {
    console.log('🔍 [SIMPLE LEAD AI] UserInfo loaded:', {
      hasUserInfo: !!userInfo,
      userId: userInfo?.id,
      firstName: userInfo?.user_first_name,
      email: userInfo?.user_email,
      loaded: !!userInfo
    });
  }, [userInfo]);

  // Initialize with welcome message and proactive workflow alerts
  useEffect(() => {
    // Only proceed if we have basic requirements AND userInfo is available
    if (leadFirstName && messages.length === 0 && userInfo && (userInfo.user_first_name || userInfo.user_email)) {
      // Context will be loaded automatically on first chat message for optimal performance
      
      // Load personalized welcome message
      const loadInitialMessage = async () => {
        console.log('📡 [LEAD AI] Starting initial message load...');
        console.log('📡 [LEAD AI] Lead context:', { leadId, leadFirstName, leadLastName, userId });
        console.log('📡 [LEAD AI] User context:', { 
          userId: userId || userInfo?.id,
          hasUserInfo: !!userInfo,
          userFirstName: userInfo?.user_first_name,
          userEmail: userInfo?.user_email
        });
        
        try {
          // Pre-load lead context for fast AI responses (daemon will cache this)
          const response = await llmAxiosInstance.get(`/lead/${leadId}/initial-message`, {
            params: { 
              user_id: userId || userInfo?.id,
              include_context: true,
              lead_name: leadFirstName
            }
          }).catch(error => {
            console.error('❌ [LEAD AI] API call failed:', {
              status: error.response?.status,
              message: error.response?.data?.message,
              url: error.config?.url
            });
            throw error;
          });
          
          console.log('✅ [LEAD AI] Initial message response:', {
            success: response.data?.success,
            hasMessage: !!response.data?.message,
            hasContext: !!response.data?.context
          });
          
          // Get user's first name from userInfo
          const userFirstName = userInfo?.user_first_name?.split(' ')[0] || userInfo?.user_email?.split('@')[0] || 'there';
          const leadFullName = `${leadFirstName} ${leadLastName || ''}`.trim() || 'this lead';
          
          const initialMessages: ChatMessage[] = [];
          
          // Always add welcome message with proper context
          const customMessage = `Hi ${userFirstName}! I'm your AI Assistant for ${leadFullName}. 
I can help you analyze their profile, suggest next steps, and answer questions about their activity.

What would you like to know about ${leadFirstName}?`;
          
          initialMessages.push({
            type: 'ai',
            message: customMessage,
            timestamp: new Date().toISOString()
          });
          
          // 🚀 PROACTIVE WORKFLOW ALERTS - Add as chat messages if present
          if (workflowAlerts && workflowAlerts.length > 0) {
            const alertMessage = generateProactiveAlertMessage(workflowAlerts, leadFirstName);
            initialMessages.push({
              type: 'ai',
              message: alertMessage,
              timestamp: new Date().toISOString()
            });
            
            console.log(`🔔 [PROACTIVE AI] Added ${workflowAlerts.length} workflow alerts to chat for ${leadFirstName}`);
          }
          
          setMessages(initialMessages);
          
        } catch (error: any) {
          console.error('❌ [LEAD AI] Failed to load initial message:', {
            error: error.message,
            response: error.response?.data,
            status: error.response?.status
          });
          // Fallback to default message with proper context
          const userFirstName = userInfo?.user_first_name?.split(' ')[0] || userInfo?.user_email?.split('@')[0] || 'there';
          const leadFullName = `${leadFirstName} ${leadLastName || ''}`.trim() || 'this lead';
          
          const initialMessages: ChatMessage[] = [];
          
          const fallbackMessage = `Hi ${userFirstName}! I'm your AI Assistant for ${leadFullName}. 
I can help you analyze their profile, suggest next steps, and answer questions about their activity.

What would you like to know about ${leadFirstName}?`;
          
          initialMessages.push({
            type: 'ai',
            message: fallbackMessage,
            timestamp: new Date().toISOString()
          });
          
          // Add workflow alerts even if welcome message fails
          if (workflowAlerts && workflowAlerts.length > 0) {
            const alertMessage = generateProactiveAlertMessage(workflowAlerts, leadFirstName);
            initialMessages.push({
              type: 'ai',
              message: alertMessage,
              timestamp: new Date().toISOString()
            });
          }
          
          setMessages(initialMessages);
        }
      };
      
      loadInitialMessage();
    }
  }, [leadFirstName, leadId, userId, messages.length, userInfo, workflowAlerts]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      type: 'user',
      message: inputValue,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputValue;
    setInputValue('');
    setIsLoading(true);

    // Add typing indicator
    const typingMessage: ChatMessage = {
      type: 'ai',
      message: `Analyzing ${leadFirstName}'s information...`,
      timestamp: new Date().toISOString(),
      isTyping: true
    };
    setMessages(prev => [...prev, typingMessage]);

    try {
      // Call the Flask AI/ML server (with /api prefix)
      // The backend now has fresh lead_ai_context data that includes proper lead information
      console.log('🤖 [LEAD AI] Sending chat message for lead:', {
        leadFirstName,
        leadLastName,
        leadId,
        originalMessage: currentInput
      });
      
      // Check if user is responding to a proactive alert and add context
      const lastBotMessage = messages.filter(msg => msg.type === 'ai').slice(-1)[0];
      const isRespondingToAlert = lastBotMessage && lastBotMessage.message.includes('Proactive Alert');
      
      let contextualMessage = currentInput;
      if (isRespondingToAlert) {
        // Extract task information from the last alert
        const taskMatch = lastBotMessage.message.match(/Task: "(.*?)"/);
        const taskName = taskMatch ? taskMatch[1] : 'workflow task';
        
        // Create comprehensive context that includes the original alert and lead information
        const fullAlertContext = lastBotMessage.message;
        
        contextualMessage = `[IMPORTANT CONTEXT: The user is responding to this proactive workflow alert:
"${fullAlertContext}"

Key Points:
- This is about a REAL ESTATE LEAD (person) named "${leadFirstName}${leadLastName ? ' ' + leadLastName : ''}"
- NOT about ${leadFirstName} as a city or location
- The user wants help with the workflow task: "${taskName}"
- This is a CRM/lead management conversation, not a geographic/location discussion]

User's Response: ${currentInput}`;
        
        console.log('🔔 [PROACTIVE CONTEXT] Adding comprehensive alert context:', {
          originalMessage: currentInput,
          taskName,
          leadName: `${leadFirstName}${leadLastName ? ' ' + leadLastName : ''}`,
          fullContext: contextualMessage
        });
      }

      console.log('🤖 [LEAD AI] Sending chat message for lead:', {
        leadFirstName,
        leadLastName,
        leadId,
        originalMessage: currentInput,
        hasAlertContext: isRespondingToAlert,
        leadFullName: `${leadFirstName} ${leadLastName || ''}`.trim()
      });

      const response = await llmAxiosInstance.post('/chat/internal/lead', {
        message: contextualMessage,
        lead_id: leadId,
        user_id: userId || userInfo?.id,
        lead_context: {
          lead_name: `${leadFirstName} ${leadLastName || ''}`.trim(),
          lead_first_name: leadFirstName,
          lead_last_name: leadLastName || '',
          is_person: true,
          context_type: 'lead_analysis'
        },
        conversationHistory: messages.filter(msg => msg.type !== 'ai' || !msg.isTyping).map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.message
        }))
      });

      const aiResponse = response.data?.response || response.data?.message || "I'm here to help with your lead analysis!";
      
      // Remove typing indicator and add real response
      setMessages(prev => {
        const filtered = prev.filter(msg => !msg.isTyping);
        return [...filtered, {
          type: 'ai',
          message: aiResponse,
          timestamp: new Date().toISOString()
        }];
      });

    } catch (error: any) {
      console.error('Lead AI Chat Error:', error);
      
      // Remove typing indicator and add error message
      setMessages(prev => {
        const filtered = prev.filter(msg => !msg.isTyping);
        return [...filtered, {
          type: 'ai',
          message: `Sorry, I'm having trouble connecting right now. Please try again in a moment.`,
          timestamp: new Date().toISOString()
        }];
      });
      
      toast.error('AI Assistant temporarily unavailable');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Generate proactive alert message for chat
  const generateProactiveAlertMessage = (alerts: WorkflowAlert[], leadName: string): string => {
    const criticalAlerts = alerts.filter(a => a.priority === 'critical');
    const highAlerts = alerts.filter(a => a.priority === 'high');
    const otherAlerts = alerts.filter(a => !['critical', 'high'].includes(a.priority));
    
    let message = `🔔 **Proactive Alert for ${leadName}**\n\n`;
    message += `I noticed ${alerts.length} workflow task${alerts.length > 1 ? 's' : ''} that need${alerts.length === 1 ? 's' : ''} your attention:\n\n`;
    
    // Critical alerts first (overdue)
    if (criticalAlerts.length > 0) {
      message += '🚨 **CRITICAL - OVERDUE:**\n';
      criticalAlerts.forEach(alert => {
        message += `• ${alert.message}\n`;
        message += `  Type: ${alert.workflow_type} | Progress: ${alert.metadata.workflow_progress}%\n`;
      });
      message += '\n';
    }
    
    // High priority alerts (due soon)
    if (highAlerts.length > 0) {
      message += '⚠️ **HIGH PRIORITY - DUE SOON:**\n';
      highAlerts.forEach(alert => {
        message += `• ${alert.message}\n`;
        message += `  Type: ${alert.workflow_type} | Progress: ${alert.metadata.workflow_progress}%\n`;
      });
      message += '\n';
    }
    
    // Other alerts
    if (otherAlerts.length > 0) {
      message += '📅 **UPCOMING:**\n';
      otherAlerts.forEach(alert => {
        message += `• ${alert.message}\n`;
      });
      message += '\n';
    }
    
    message += `💡 **Need help with any of these tasks?** Just ask me! I can provide context about ${leadName}, suggest next steps, or help you prioritize your actions.`;
    
    return message;
  };
  
  // Handle workflow action buttons in chat
  const handleWorkflowAction = (actionType: string, alert: WorkflowAlert) => {
    if (onWorkflowAction) {
      onWorkflowAction(alert, actionType);
    }
    
    // Add a chat message confirming the action
    const actionMessage: ChatMessage = {
      type: 'ai',
      message: `✅ Action completed: ${actionType.replace('_', ' ')} for ${alert.workflow_type}. I've updated your workflow status.`,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, actionMessage]);
  };

  const suggestions = [
    `What should I know about ${leadFirstName}?`,
    `Next steps for ${leadFirstName}?`,
    'Generate follow-up email'
  ];

  return (
    <div className="simple-lead-ai-container">
      {/* Toggle Button */}
      {!isOpen && (
        <button 
          className="simple-ai-toggle"
          onClick={() => setIsOpen(true)}
          title="Open Lead AI Assistant"
        >
          <FontAwesomeIcon icon={faComments} className="me-2" />
          <span>Lead AI Assistant</span>
          {workflowAlerts && workflowAlerts.length > 0 && (
            <div className="ai-pulse urgent-pulse"></div>
          )}
          {(!workflowAlerts || workflowAlerts.length === 0) && (
            <div className="ai-pulse"></div>
          )}
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div className="simple-ai-panel">
          {/* Header */}
          <div className="simple-ai-header">
            <div className="d-flex align-items-center">
              <div className="ai-avatar">
                <img 
                  src={KairosLogo} 
                  alt="KairosIQ AI" 
                  style={{
                    width: '28px',
                    height: '28px',
                    objectFit: 'cover',
                    filter: 'brightness(1.1) contrast(1.2)',
                    backgroundColor: 'white',
                    padding: '2px',
                    borderRadius: '6px',
                    border: '1px solid rgba(0,0,0,0.1)'
                  }}
                />
              </div>
              <div className="ms-2">
                <h6 className="mb-0">LeadIQ AI Assistant</h6>
                <small>{leadFirstName} {leadLastName || ''}</small>
              </div>
            </div>
            <button 
              className="btn btn-link p-1"
              onClick={() => setIsOpen(false)}
              title="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Messages */}
          <div className="simple-ai-messages">
            {messages.map((msg, index) => (
              <div key={index} className={`simple-message ${msg.type}`}>
                {msg.type === 'ai' && (
                  <div className="ai-avatar-small">
                    <img 
                      src={KairosLogo} 
                      alt="KairosIQ AI" 
                      style={{
                        width: '22px',
                        height: '22px',
                        objectFit: 'cover',
                        filter: 'brightness(1.1) contrast(1.2)',
                        backgroundColor: 'white',
                        padding: '2px',
                        borderRadius: '4px',
                        border: '1px solid rgba(0,0,0,0.1)'
                      }}
                    />
                  </div>
                )}
                <div className="message-bubble">
                  <p className={msg.isTyping ? 'typing' : ''}>{msg.message}</p>
                  <small className="timestamp">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </small>
                </div>
              </div>
            ))}
          </div>

          {/* Suggestions */}
          <div className="simple-ai-suggestions">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                className="suggestion-btn"
                onClick={() => {
                  setInputValue(suggestion);
                  setTimeout(handleSendMessage, 100);
                }}
                disabled={isLoading}
              >
                {suggestion}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="simple-ai-input">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder={`Ask about ${leadFirstName}...`}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
              />
              <button
                className="btn btn-primary"
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .simple-lead-ai-container {
          position: fixed;
          bottom: 20px;
          right: 20px;
          z-index: 9999;
        }

        .simple-ai-toggle {
          background: linear-gradient(135deg, #198754 0%, #20c997 100%);
          color: white;
          border: none;
          border-radius: 25px;
          padding: 12px 20px;
          cursor: pointer;
          box-shadow: 0 4px 15px rgba(25, 135, 84, 0.3);
          transition: all 0.3s ease;
          position: relative;
        }

        .simple-ai-toggle:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(25, 135, 84, 0.4);
        }

        .ai-pulse {
          position: absolute;
          top: -2px;
          right: -2px;
          width: 10px;
          height: 10px;
          background: #ffc107;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.2); }
        }

        .simple-ai-panel {
          width: 350px;
          height: 500px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border: 1px solid #e9ecef;
        }

        .simple-ai-header {
          background: linear-gradient(135deg, #198754 0%, #20c997 100%);
          color: white;
          padding: 15px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .ai-avatar {
          width: 30px;
          height: 30px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .simple-ai-header h6 {
          color: white;
          margin: 0;
          font-weight: 600;
        }

        .simple-ai-header small {
          color: rgba(255, 255, 255, 0.8);
        }

        .simple-ai-header .btn-link {
          color: white !important;
          text-decoration: none;
        }

        .simple-ai-messages {
          flex: 1;
          padding: 20px;
          overflow-y: auto;
          background: #f8f9fa;
        }

        .simple-message {
          display: flex;
          margin-bottom: 15px;
          align-items: flex-start;
        }

        .simple-message.user {
          justify-content: flex-end;
        }

        .ai-avatar-small {
          width: 24px;
          height: 24px;
          background: linear-gradient(135deg, #198754 0%, #20c997 100%);
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 10px;
          flex-shrink: 0;
        }

        .message-bubble {
          max-width: 70%;
          padding: 12px 16px;
          border-radius: 16px;
          margin: 0 8px;
        }

        .simple-message.user .message-bubble {
          background: #007bff;
          color: white;
          border-radius: 16px 16px 4px 16px;
        }

        .simple-message.ai .message-bubble {
          background: white;
          color: #333;
          border: 1px solid #e9ecef;
          border-radius: 16px 16px 16px 4px;
        }

        .message-bubble p {
          margin: 0;
          line-height: 1.4;
        }

        .typing {
          font-style: italic;
          opacity: 0.7;
        }

        .timestamp {
          color: #6c757d;
          font-size: 11px;
          margin-top: 4px;
          display: block;
        }

        .simple-ai-suggestions {
          padding: 10px 15px;
          background: white;
          border-top: 1px solid #e9ecef;
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .suggestion-btn {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 12px;
          padding: 6px 12px;
          font-size: 12px;
          color: #495057;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .suggestion-btn:hover:not(:disabled) {
          background: #198754;
          color: white;
          border-color: #198754;
        }

        .suggestion-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .simple-ai-input {
          padding: 15px 20px;
          background: white;
          border-top: 1px solid #e9ecef;
        }

        .simple-ai-input .form-control {
          border-radius: 20px;
          border: 1px solid #e9ecef;
          padding: 10px 15px;
        }

        .simple-ai-input .btn-primary {
          border-radius: 20px;
          padding: 10px 15px;
          background: linear-gradient(135deg, #198754 0%, #20c997 100%);
          border: none;
        }

        @media (max-width: 768px) {
          .simple-ai-panel {
            width: calc(100vw - 40px);
            height: 400px;
          }
        }
        
        .urgent-pulse {
          background: #dc3545 !important;
          animation: urgentPulse 1s infinite !important;
          box-shadow: 0 0 10px rgba(220, 53, 69, 0.5);
        }
        
        @keyframes urgentPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.4); }
        }
      `}</style>
    </div>
  );
};

export default SimpleLeadAI;